import { EventBus, Events } from './EventBus';
import { StateManager } from './StateManager';
import type { CombatEvent, ServerCombatState, AnimationDef, ConfigData } from '../types';

const DEFAULT_ANIM: AnimationDef = {
  id: '_default',
  name: 'Default',
  castMotion: 'lunge',
  hitEffect: 'slash',
  tint: 0xffffff,
};

/** Time between hits of a multi-hit flurry. Slow enough each beat reads as distinct, fast enough to feel like a combo. */
const MULTIHIT_BEAT_MS = 240;

/**
 * Per-hit-index x-offsets for the floating-number cascade. Wide enough that 28px-font
 * damage numbers don't horizontally overlap a neighbor (each number is ~50-60px wide).
 */
const MULTIHIT_X_OFFSETS = [-45, 45, 0, -55, 55, -25, 25, 0, -60, 60];

/**
 * Per-hit vertical climb. Each subsequent hit's number spawns higher up the screen so
 * the cascade reads as a stack ("1, 2, 3" rising column) instead of three numbers
 * fighting for the same vertical strip while float-up barely moves them apart.
 */
const MULTIHIT_Y_OFFSET_PER_HIT = -22;

/** Per-source tint for barrier shatter effects (matches BARRIER_COLORS in CombatHUD) */
const BARRIER_TINTS: Record<string, number> = {
  brinewall:          0x66ccff,
  tideguard:          0x1166bb,
  iron_wall:          0xcc9944,
  battleward:         0x88aacc,
  turn_start_barrier: 0x22aa88,
};
function barrierTintForSource(sourceId: string | undefined): number {
  if (!sourceId) return 0x66ccff;
  return BARRIER_TINTS[sourceId] ?? 0x66ccff;
}

/** Event types whose `message` should NOT surface in the combat log — the
 *  visual (bar fill, pip update) is the feedback. Server still emits the
 *  string for tests/debug. */
const NO_LOG_EVENT_TYPES = new Set<string>(['break_fill']);

class EventPlaybackManagerClass {
  private _isPlaying = false;
  /** sourceId -> targetId of in-flight charged casts. Used to clear the targeted
   *  visual on the victim once the cast resolves into damage/heal. */
  private castingTargets = new Map<string, string>();

  get isPlaying(): boolean {
    return this._isPlaying;
  }

  private clearCastTarget(sourceId: string): void {
    const targetId = this.castingTargets.get(sourceId);
    if (!targetId) return;
    this.castingTargets.delete(sourceId);
    EventBus.emit(Events.COMBAT_CAST_TARGET_EXIT, { sourceId, targetId });
  }

  private clearAllCastTargets(): void {
    for (const [sourceId, targetId] of this.castingTargets) {
      EventBus.emit(Events.COMBAT_CAST_TARGET_EXIT, { sourceId, targetId });
    }
    this.castingTargets.clear();
  }

  async playEvents(events: CombatEvent[], combatState: ServerCombatState): Promise<void> {
    this._isPlaying = true;

    let i = 0;
    while (i < events.length) {
      const event = events[i];

      // Batch consecutive damage/heal events from the same source+ability.
      // Three shapes:
      //   - Single target with N events  → multi-hit single target (sequential, 180ms beats)
      //   - Multiple targets, 1 event each → AoE single hit (simultaneous, current behavior)
      //   - Multiple targets, multi-hit  → AoE multi-hit waves (transposed, 180ms between waves)
      if ((event.type === 'damage' || event.type === 'heal') && event.abilityId) {
        const batch = [event];
        while (
          i + batch.length < events.length &&
          events[i + batch.length].type === event.type &&
          events[i + batch.length].sourceId === event.sourceId &&
          events[i + batch.length].abilityId === event.abilityId
        ) {
          batch.push(events[i + batch.length]);
        }

        if (batch.length > 1) {
          // Bucket events by targetId to detect multi-hit vs AoE.
          const buckets = new Map<string, CombatEvent[]>();
          for (const ev of batch) {
            const key = ev.targetId ?? '';
            const list = buckets.get(key);
            if (list) list.push(ev);
            else buckets.set(key, [ev]);
          }
          const targetCount = buckets.size;
          const maxHitsPerTarget = Math.max(...Array.from(buckets.values()).map(b => b.length));

          if (targetCount === 1) {
            await this.playMultiHitSingleTarget(batch, combatState);
          } else if (maxHitsPerTarget > 1) {
            await this.playAoeMultiHitWaves(buckets, batch[0], combatState);
          } else {
            await this.playAoeBatch(batch, combatState);
          }
          await this.delay(this.getDelay(event));
          i += batch.length;
          continue;
        }
      }

      // Batch consecutive debuff events with the same statusEffectId (AoE debuff application)
      if (event.type === 'debuff') {
        const batch = [event];
        while (
          i + batch.length < events.length &&
          events[i + batch.length].type === 'debuff' &&
          events[i + batch.length].statusEffectId === event.statusEffectId &&
          events[i + batch.length].sourceId === event.sourceId
        ) {
          batch.push(events[i + batch.length]);
        }

        if (batch.length > 1) {
          await this.playDebuffBatch(batch, combatState);
          await this.delay(this.getDelay(event));
          i += batch.length;
          continue;
        }
      }

      // Batch consecutive dot_tick events with the same statusEffectId (start-of-turn ticks)
      if (event.type === 'dot_tick') {
        const batch = [event];
        while (
          i + batch.length < events.length &&
          events[i + batch.length].type === 'dot_tick' &&
          events[i + batch.length].statusEffectId === event.statusEffectId
        ) {
          batch.push(events[i + batch.length]);
        }

        if (batch.length > 1) {
          await this.playDotTickBatch(batch, combatState);
          await this.delay(this.getDelay(event));
          i += batch.length;
          continue;
        }
      }

      await this.playEvent(event, combatState);
      await this.delay(this.getDelay(event));
      i++;
    }

    this._isPlaying = false;
    EventBus.emit(Events.COMBAT_PLAYBACK_COMPLETE);
  }

  /** Resolve the attacker's side from a sourceId. Used to color log rows by who's
   *  attacking — without this every damage row is the same red and you can't tell
   *  your hits from incoming hits at a glance. */
  private getSourceSide(sourceId: string | undefined, state: ServerCombatState): 'player' | 'enemy' | undefined {
    if (!sourceId) return undefined;
    return state.combatants.find(c => c.id === sourceId)?.side;
  }

  /** Play a batch of AoE damage/heal events: cast once, hit all targets simultaneously */
  private async playAoeBatch(batch: CombatEvent[], state: ServerCombatState): Promise<void> {
    const first = batch[0];
    const anim = this.resolveAnimation(first);

    // Log all events
    for (const event of batch) {
      EventBus.emit(Events.COMBAT_LOG, {
        message: event.message,
        type: event.type,
        isCrit: event.isCrit,
        sourceSide: this.getSourceSide(event.sourceId, state),
      });
    }

    // Cast phase — single attacker animation
    EventBus.emit(Events.COMBAT_ABILITY_USED, {
      sourceId: first.sourceId,
      animation: anim,
    });
    await this.delay(anim.castTime ?? 200);

    // Hit phase — all targets simultaneously
    for (const event of batch) {
      const target = state.combatants.find(c => c.id === event.targetId);
      if (!target) continue;

      EventBus.emit(Events.COMBAT_HIT, {
        targetId: event.targetId,
        animation: anim,
        side: target.side,
      });

      if (first.type === 'damage') {
        EventBus.emit(Events.FLOATING_DAMAGE, { targetId: event.targetId, value: event.value, isCrit: event.isCrit, elementMultiplier: event.elementMultiplier });
        EventBus.emit(Events.HP_UPDATE, { id: event.targetId, hp: event.hpAfter ?? target.hp, shield: event.shieldAfter, barriers: event.barriersAfter, focus: event.focusAfter });
      } else {
        EventBus.emit(Events.FLOATING_HEAL, { targetId: event.targetId, value: event.value });
        EventBus.emit(Events.HP_UPDATE, { id: event.targetId, hp: event.hpAfter ?? target.hp, focus: event.focusAfter });
      }
    }
  }

  /**
   * Multi-hit on a single target — Pugilism Jab x3, Palm Strike x5, Rift Gust x3, etc.
   * Cast plays once (action sprite holds for the full flurry); each hit lands as its
   * own beat with 180ms between beats. Crit/super-effective/resist flashes fire per-hit
   * so the player can see exactly which hit critted. Floating numbers cascade with a
   * deterministic xOffset pattern for readability.
   */
  private async playMultiHitSingleTarget(batch: CombatEvent[], state: ServerCombatState): Promise<void> {
    const first = batch[0];
    if (!first.targetId) return;
    const target = state.combatants.find(c => c.id === first.targetId);
    if (!target) return;
    const anim = this.resolveAnimation(first);

    // Hold the action sprite for the full flurry (cast + all beats + a small tail).
    const castTime = anim.castTime ?? 200;
    const flurryHoldMs = castTime + (batch.length - 1) * MULTIHIT_BEAT_MS + 200;

    EventBus.emit(Events.COMBAT_ABILITY_USED, {
      sourceId: first.sourceId,
      animation: { ...anim, actionHoldMs: Math.max(anim.actionHoldMs ?? 0, flurryHoldMs) },
    });
    await this.delay(castTime);

    const sourceSide = this.getSourceSide(first.sourceId, state);
    for (let hitIdx = 0; hitIdx < batch.length; hitIdx++) {
      const ev = batch[hitIdx];
      this.playMultiHitBeat(ev, target.side, anim, hitIdx, first.type === 'heal', sourceSide);
      if (hitIdx < batch.length - 1) await this.delay(MULTIHIT_BEAT_MS);
    }
  }

  /**
   * AoE multi-hit — buckets events by target, transposes to per-hit-index waves so all
   * targets get hit-1 simultaneously, then 180ms later all get hit-2, etc. No current
   * ability uses this combo, but the path is here for future cleaves/storms.
   * Note: a target may die mid-flurry (server stops emitting further hits for it),
   * so buckets can have unequal lengths — we just skip targets without an event at
   * the current wave-index.
   */
  private async playAoeMultiHitWaves(
    buckets: Map<string, CombatEvent[]>,
    first: CombatEvent,
    state: ServerCombatState,
  ): Promise<void> {
    const anim = this.resolveAnimation(first);
    const maxHits = Math.max(...Array.from(buckets.values()).map(b => b.length));

    // Compute hold across the full multi-wave flurry so the cast sprite stays out.
    const castTime = anim.castTime ?? 200;
    const flurryHoldMs = castTime + (maxHits - 1) * MULTIHIT_BEAT_MS + 200;

    EventBus.emit(Events.COMBAT_ABILITY_USED, {
      sourceId: first.sourceId,
      animation: { ...anim, actionHoldMs: Math.max(anim.actionHoldMs ?? 0, flurryHoldMs) },
    });
    await this.delay(castTime);

    const sourceSide = this.getSourceSide(first.sourceId, state);
    for (let waveIdx = 0; waveIdx < maxHits; waveIdx++) {
      for (const events of buckets.values()) {
        const ev = events[waveIdx];
        if (!ev || !ev.targetId) continue;
        const target = state.combatants.find(c => c.id === ev.targetId);
        if (!target) continue;
        this.playMultiHitBeat(ev, target.side, anim, waveIdx, first.type === 'heal', sourceSide);
      }
      if (waveIdx < maxHits - 1) await this.delay(MULTIHIT_BEAT_MS);
    }
  }

  /** Single beat of a multi-hit flurry: log + impact + crit/resist flash + floating number + HP. */
  private playMultiHitBeat(
    ev: CombatEvent,
    targetSide: 'player' | 'enemy',
    anim: AnimationDef,
    hitIdx: number,
    isHeal: boolean,
    sourceSide?: 'player' | 'enemy',
  ): void {
    EventBus.emit(Events.COMBAT_LOG, { message: ev.message, type: ev.type, isCrit: ev.isCrit, sourceSide });
    if (!ev.targetId) return;

    EventBus.emit(Events.COMBAT_HIT, {
      targetId: ev.targetId,
      animation: anim,
      side: targetSide,
    });

    if (!isHeal) {
      if (ev.isCrit) {
        EventBus.emit(Events.COMBAT_HIT, {
          targetId: ev.targetId,
          animation: { hitEffect: 'crit_flash', tint: 0xffffff },
          side: targetSide,
        });
      }
      if (ev.elementMultiplier != null && ev.elementMultiplier >= 1.3) {
        EventBus.emit(Events.COMBAT_HIT, {
          targetId: ev.targetId,
          animation: { hitEffect: 'super_effective', tint: anim.tint ?? 0xffdd44 },
          side: targetSide,
        });
      } else if (ev.elementMultiplier != null && ev.elementMultiplier <= 0.7) {
        EventBus.emit(Events.COMBAT_HIT, {
          targetId: ev.targetId,
          animation: { hitEffect: 'resist_flash', tint: 0x888888 },
          side: targetSide,
        });
      }
      EventBus.emit(Events.FLOATING_DAMAGE, {
        targetId: ev.targetId,
        value: ev.value,
        isCrit: ev.isCrit,
        elementMultiplier: ev.elementMultiplier,
        xOffset: MULTIHIT_X_OFFSETS[hitIdx % MULTIHIT_X_OFFSETS.length],
        yOffset: hitIdx * MULTIHIT_Y_OFFSET_PER_HIT,
      });
    } else {
      EventBus.emit(Events.FLOATING_HEAL, { targetId: ev.targetId, value: ev.value });
    }

    EventBus.emit(Events.HP_UPDATE, {
      id: ev.targetId,
      hp: ev.hpAfter,
      shield: ev.shieldAfter,
      barriers: ev.barriersAfter,
      focus: ev.focusAfter,
    });
  }

  /** Play a batch of debuff events: status icon + flash on all targets simultaneously */
  private async playDebuffBatch(batch: CombatEvent[], state: ServerCombatState): Promise<void> {
    const first = batch[0];
    const hitEffect = this.getDebuffHitEffect(first.statusEffectId);
    const tint = this.getDebuffTint(first.statusEffectId);

    for (const event of batch) {
      EventBus.emit(Events.COMBAT_LOG, {
        message: event.message,
        type: event.type,
        isCrit: event.isCrit,
        sourceSide: this.getSourceSide(event.sourceId, state),
      });
    }

    for (const event of batch) {
      const target = state.combatants.find(c => c.id === event.targetId);
      if (!target) continue;
      EventBus.emit(Events.COMBAT_HIT, {
        targetId: target.id,
        animation: { hitEffect, tint },
        side: target.side,
      });
      EventBus.emit(Events.STATUS_EFFECTS_UPDATE, { id: target.id, statusEffects: target.statusEffects });
    }
  }

  /** Play a batch of dot_tick events: tick damage on all targets simultaneously */
  private async playDotTickBatch(batch: CombatEvent[], state: ServerCombatState): Promise<void> {
    const first = batch[0];
    const hitEffect = this.getDotHitEffect(first.statusEffectId);
    const tint = this.getDotTint(first.statusEffectId);

    for (const event of batch) {
      // DoT ticks have no sourceId on the event itself — color them by who's
      // taking the damage instead so player-facing burns/bleeds read distinct
      // from damage you're inflicting on a DoT'd enemy.
      const target = state.combatants.find(c => c.id === event.targetId);
      EventBus.emit(Events.COMBAT_LOG, {
        message: event.message,
        type: event.type,
        isCrit: event.isCrit,
        sourceSide: target?.side === 'player' ? 'enemy' : 'player',
      });
    }

    for (const event of batch) {
      const target = state.combatants.find(c => c.id === event.targetId);
      if (!target) continue;
      EventBus.emit(Events.FLOATING_DOT, { targetId: event.targetId, value: event.value });
      EventBus.emit(Events.HP_UPDATE, { id: event.targetId, hp: event.hpAfter ?? target.hp, shield: event.shieldAfter, barriers: event.barriersAfter, focus: event.focusAfter });
      EventBus.emit(Events.COMBAT_HIT, {
        targetId: event.targetId,
        animation: { hitEffect, tint },
        side: target.side,
      });
    }
  }

  private resolveAnimation(event: CombatEvent): AnimationDef {
    const configData = StateManager.get('configData') as ConfigData | null;

    // Direct animation key override (e.g. from cast_start/cast_tick events)
    if (event.animationKey && configData) {
      const direct = configData.animations?.[event.animationKey];
      if (direct) return direct;
    }

    if (!event.abilityId) return DEFAULT_ANIM;
    if (!configData) return DEFAULT_ANIM;

    const ability = configData.abilities?.[event.abilityId];
    if (!ability?.animationKey) return DEFAULT_ANIM;

    return configData.animations?.[ability.animationKey] ?? DEFAULT_ANIM;
  }

  private async playEvent(event: CombatEvent, state: ServerCombatState): Promise<void> {
    // Add to battle log — except for noisy per-tick event types whose visual
    // (bar fill, pip update, etc) carries the feedback. The server still
    // sets a `.message` for tests/debug; we just suppress at the client.
    if (!NO_LOG_EVENT_TYPES.has(event.type)) {
      EventBus.emit(Events.COMBAT_LOG, {
        message: event.message,
        type: event.type,
        isCrit: event.isCrit,
        sourceSide: this.getSourceSide(event.sourceId, state),
      });
    }

    switch (event.type) {
      case 'turn_start': {
        // Reshuffle the timeline strip BEFORE highlighting the new actor, so the
        // new order (e.g. post-stagger) is visible before the actor moves.
        if (event.timeline && event.sourceId) {
          EventBus.emit(Events.COMBAT_TIMELINE_RESORT, { timeline: event.timeline, activeId: event.sourceId });
        }
        EventBus.emit(Events.ACTIVE_TURN_CHANGED, event.sourceId);
        const turnCombatant = state.combatants.find(c => c.id === event.sourceId);
        // Update focus bar + clear tempo badge (tempo resets at turn start)
        // Do NOT pass hp — state.combatants has FINAL hp which causes desync
        if (event.sourceId) {
          EventBus.emit(Events.HP_UPDATE, { id: event.sourceId, focus: event.focusAfter, tempo: 0 });
        }
        if (turnCombatant?.side === 'enemy') {
          await this.delay(300);
        }
        break;
      }

      case 'damage': {
        const target = state.combatants.find(c => c.id === event.targetId);
        if (!target) break;

        // If this damage is the resolution of a charged cast from a tracked
        // source, clear that source's threat reticle BEFORE the hit plays so
        // the targeted glow doesn't linger over the impact.
        if (event.sourceId) this.clearCastTarget(event.sourceId);

        const anim = this.resolveAnimation(event);

        // Cast phase — attacker animation
        EventBus.emit(Events.COMBAT_ABILITY_USED, {
          sourceId: event.sourceId,
          animation: anim,
        });
        await this.delay(anim.castTime ?? 200);

        // Hit phase — impact on target
        EventBus.emit(Events.COMBAT_HIT, {
          targetId: event.targetId,
          animation: anim,
          side: target.side,
        });

        // Reactive overlays: crit flash, super effective, resist
        if (event.isCrit) {
          EventBus.emit(Events.COMBAT_HIT, {
            targetId: event.targetId,
            animation: { hitEffect: 'crit_flash', tint: 0xffffff },
            side: target.side,
          });
        }
        if (event.elementMultiplier != null && event.elementMultiplier >= 1.3) {
          EventBus.emit(Events.COMBAT_HIT, {
            targetId: event.targetId,
            animation: { hitEffect: 'super_effective', tint: anim.tint ?? 0xffdd44 },
            side: target.side,
          });
        } else if (event.elementMultiplier != null && event.elementMultiplier <= 0.7) {
          EventBus.emit(Events.COMBAT_HIT, {
            targetId: event.targetId,
            animation: { hitEffect: 'resist_flash', tint: 0x888888 },
            side: target.side,
          });
        }

        // Show floating damage text + update HP bar.
        // Tempo is NOT passed here — tempo_gain events handle their own updates so the
        // pill doesn't jump to the final value on first hit of a multi-hit chain.
        EventBus.emit(Events.FLOATING_DAMAGE, { targetId: event.targetId, value: event.value, isCrit: event.isCrit, elementMultiplier: event.elementMultiplier });
        EventBus.emit(Events.HP_UPDATE, { id: event.targetId, hp: event.hpAfter ?? target.hp, shield: event.shieldAfter, barriers: event.barriersAfter, focus: event.focusAfter });
        break;
      }

      case 'heal': {
        const healTarget = state.combatants.find(c => c.id === event.targetId);
        if (!healTarget) break;

        // Clear charged-cast reticle if this heal resolves a player charged cast.
        if (event.sourceId) this.clearCastTarget(event.sourceId);

        // Passive heals (Life on Hit, Leech, Soulstitch, Regen ticks, status-effect regen)
        // never have an abilityId — they're not a cast. Show a subtle tick on the target,
        // no cast motion / texture swap / caster glow on the source. Otherwise we render
        // a phantom "second cast" right after the actor's attack.
        const isPassiveHeal = !event.abilityId;
        if (isPassiveHeal) {
          EventBus.emit(Events.COMBAT_HIT, {
            targetId: healTarget.id,
            animation: { hitEffect: 'regen_tick', tint: 0x88ff88 },
            side: healTarget.side,
          });
          EventBus.emit(Events.FLOATING_HEAL, { targetId: event.targetId, value: event.value });
          EventBus.emit(Events.HP_UPDATE, { id: event.targetId, hp: event.hpAfter ?? healTarget.hp, focus: event.focusAfter });
          break;
        }

        const anim = this.resolveAnimation(event);

        // Cast phase — caster animation
        EventBus.emit(Events.COMBAT_ABILITY_USED, {
          sourceId: event.sourceId,
          animation: anim,
        });
        await this.delay(anim.castTime ?? 200);

        // Hit phase on target
        EventBus.emit(Events.COMBAT_HIT, {
          targetId: event.targetId,
          animation: anim,
          side: healTarget.side,
        });

        EventBus.emit(Events.FLOATING_HEAL, { targetId: event.targetId, value: event.value });
        EventBus.emit(Events.HP_UPDATE, { id: event.targetId, hp: event.hpAfter ?? healTarget.hp, focus: event.focusAfter });
        break;
      }

      case 'buff': {
        const buffTarget = state.combatants.find(c => c.id === (event.targetId ?? event.sourceId));
        if (buffTarget) {
          // Use ability's animation if available (e.g. charge_glow for karlo_charged_slam)
          const anim = this.resolveAnimation(event);
          const isAbilityDriven = anim !== DEFAULT_ANIM;

          if (isAbilityDriven) {
            // Full cast+hit sequence for ability-driven buffs (charge windup etc.)
            EventBus.emit(Events.COMBAT_ABILITY_USED, {
              sourceId: event.sourceId,
              animation: anim,
            });
            await this.delay(anim.castTime ?? 200);
            EventBus.emit(Events.COMBAT_HIT, {
              targetId: buffTarget.id,
              animation: anim,
              side: buffTarget.side,
            });
          } else {
            // Per-buff visual for non-ability buffs; fallback to generic shimmer
            EventBus.emit(Events.COMBAT_HIT, {
              targetId: buffTarget.id,
              animation: { hitEffect: this.getBuffHitEffect(event.statusEffectId), tint: this.getBuffTint(event.statusEffectId) },
              side: buffTarget.side,
            });
          }
          EventBus.emit(Events.STATUS_EFFECTS_UPDATE, { id: buffTarget.id, statusEffects: buffTarget.statusEffects });
          // Barrier buff: update HP bar with new barrier instances + show floating text
          if (event.shieldValue != null && event.shieldValue > 0) {
            EventBus.emit(Events.HP_UPDATE, { id: buffTarget.id, hp: buffTarget.hp, shield: event.shieldValue, barriers: event.barriersAfter, focus: event.focusAfter });
            EventBus.emit(Events.FLOATING_INFO, { targetId: buffTarget.id, text: `Barrier +${event.shieldValue}` });
          }
        }
        break;
      }

      case 'dot_tick': {
        const dotTarget = state.combatants.find(c => c.id === event.targetId);
        if (!dotTarget) break;
        EventBus.emit(Events.FLOATING_DOT, { targetId: event.targetId, value: event.value });
        EventBus.emit(Events.HP_UPDATE, { id: event.targetId, hp: event.hpAfter ?? dotTarget.hp, shield: event.shieldAfter, barriers: event.barriersAfter, focus: event.focusAfter });
        const dotHitEffect = this.getDotHitEffect(event.statusEffectId);
        EventBus.emit(Events.COMBAT_HIT, {
          targetId: event.targetId,
          animation: { hitEffect: dotHitEffect, tint: this.getDotTint(event.statusEffectId) },
          side: dotTarget.side,
        });
        break;
      }

      case 'debuff': {
        const debuffTarget = state.combatants.find(c => c.id === event.targetId);
        if (debuffTarget) {
          // Ability-driven debuff (e.g. Powderkeg trap-plant): play full cast+hit
          // sequence so the caster's animation reads instead of a generic flash.
          const anim = this.resolveAnimation(event);
          const isAbilityDriven = anim !== DEFAULT_ANIM;

          if (isAbilityDriven) {
            EventBus.emit(Events.COMBAT_ABILITY_USED, {
              sourceId: event.sourceId,
              animation: anim,
            });
            await this.delay(anim.castTime ?? 200);
            EventBus.emit(Events.COMBAT_HIT, {
              targetId: debuffTarget.id,
              animation: anim,
              side: debuffTarget.side,
            });
          } else {
            EventBus.emit(Events.COMBAT_HIT, {
              targetId: debuffTarget.id,
              animation: { hitEffect: this.getDebuffHitEffect(event.statusEffectId), tint: this.getDebuffTint(event.statusEffectId) },
              side: debuffTarget.side,
            });
          }
          EventBus.emit(Events.STATUS_EFFECTS_UPDATE, { id: debuffTarget.id, statusEffects: debuffTarget.statusEffects });
        }
        break;
      }

      case 'cleanse': {
        const cleanseTarget = state.combatants.find(c => c.id === event.targetId);
        if (!cleanseTarget) break;

        const anim = this.resolveAnimation(event);
        EventBus.emit(Events.COMBAT_ABILITY_USED, {
          sourceId: event.sourceId,
          animation: anim,
        });
        await this.delay(anim.castTime ?? 200);
        EventBus.emit(Events.COMBAT_HIT, {
          targetId: event.targetId,
          animation: anim,
          side: cleanseTarget.side,
        });
        EventBus.emit(Events.FLOATING_INFO, {
          targetId: event.targetId,
          text: 'Cleansed!',
        });
        EventBus.emit(Events.STATUS_EFFECTS_UPDATE, { id: cleanseTarget.id, statusEffects: cleanseTarget.statusEffects });
        break;
      }

      case 'effect_expire': {
        EventBus.emit(Events.FLOATING_INFO, {
          targetId: event.targetId,
          text: event.message,
        });
        const expireTarget = state.combatants.find(c => c.id === event.targetId);
        if (expireTarget) {
          EventBus.emit(Events.STATUS_EFFECTS_UPDATE, { id: expireTarget.id, statusEffects: expireTarget.statusEffects });
          // Barrier shatter visual when a barrier instance breaks
          if (event.barrierBreak) {
            const tint = barrierTintForSource(event.statusEffectId);
            EventBus.emit(Events.COMBAT_HIT, {
              targetId: expireTarget.id,
              animation: { hitEffect: 'barrier_shatter', tint },
              side: expireTarget.side,
            });
          }
        }
        break;
      }

      case 'enemy_transformed': {
        // Boss form transition (e.g. Velara Mourn at HP thresholds): swap the
        // enemy's sprite + name in local state, fire the scene event so
        // CombatScene re-textures the sprite, and float a transformation tag.
        if (!event.targetId || !event.spriteKey) break;
        const transforming = state.combatants.find(c => c.id === event.targetId);
        if (transforming) {
          transforming.spriteKey = event.spriteKey;
          if (event.abilityName) transforming.name = event.abilityName;
        }
        EventBus.emit(Events.COMBAT_ENEMY_TRANSFORMED, {
          id: event.targetId,
          spriteKey: event.spriteKey,
          name: event.abilityName,
          transformAnimationKey: event.transformAnimationKey,
        });
        EventBus.emit(Events.FLOATING_INFO, {
          targetId: event.targetId,
          text: 'Transforms!',
        });
        await this.delay(700);
        break;
      }

      case 'defeated':
        // Animate HP bar to 0, wait for transition, then play death animation
        EventBus.emit(Events.HP_UPDATE, { id: event.targetId, hp: event.hpAfter ?? 0 });
        // If the defeated combatant was the caster OR the target of an in-flight
        // cast, clear that reticle. (Server may retarget if the victim died, in
        // which case cast_tick re-attaches us to the new target next turn.)
        if (event.targetId) {
          this.clearCastTarget(event.targetId);
          for (const [sourceId, victimId] of this.castingTargets) {
            if (victimId === event.targetId) this.clearCastTarget(sourceId);
          }
        }
        await this.delay(250);
        EventBus.emit(Events.COMBAT_ENEMY_DEFEATED, event.targetId);
        break;

      case 'victory':
        this.clearAllCastTargets();
        EventBus.emit(Events.COMBAT_VICTORY, state.rewards);
        break;

      case 'defeat':
        this.clearAllCastTargets();
        EventBus.emit(Events.COMBAT_DEFEAT);
        break;

      case 'flee_success':
        this.clearAllCastTargets();
        EventBus.emit(Events.COMBAT_FLEE_SUCCESS);
        break;

      case 'cast_start': {
        const castSource = state.combatants.find(c => c.id === event.sourceId);
        if (castSource) {
          const anim = this.resolveAnimation(event);
          const isAbilityDriven = anim !== DEFAULT_ANIM;

          if (isAbilityDriven) {
            // Subtle cast animation — no action sprite swap during charging
            EventBus.emit(Events.COMBAT_ABILITY_USED, {
              sourceId: event.sourceId,
              animation: anim,
              isCharging: true,
            });
            await this.delay(anim.castTime ?? 200);
            EventBus.emit(Events.COMBAT_HIT, {
              targetId: castSource.id,
              animation: anim,
              side: castSource.side,
            });
          }
          EventBus.emit(Events.FLOATING_INFO, {
            targetId: event.sourceId,
            text: 'Charging!',
          });

          // Telegraph the victim. targetId may be the caster itself for self-casts;
          // skip in that case so the threat reticle only fires for outgoing attacks.
          if (event.targetId && event.targetId !== event.sourceId) {
            const victim = state.combatants.find(c => c.id === event.targetId);
            if (victim) {
              this.castingTargets.set(event.sourceId!, event.targetId);
              EventBus.emit(Events.COMBAT_CAST_TARGET_ENTER, {
                sourceId: event.sourceId,
                targetId: event.targetId,
                tint: anim.tint ?? 0xff4444,
                side: victim.side,
              });
              EventBus.emit(Events.FLOATING_INFO, {
                targetId: event.targetId,
                text: 'TARGETED!',
              });
            }
          }
        }
        break;
      }

      case 'cast_tick': {
        const tickSource = state.combatants.find(c => c.id === event.sourceId);
        if (tickSource) {
          const anim = this.resolveAnimation(event);
          const isAbilityDriven = anim !== DEFAULT_ANIM;

          if (isAbilityDriven) {
            // Subtle cast animation — no action sprite swap during charging
            EventBus.emit(Events.COMBAT_ABILITY_USED, {
              sourceId: event.sourceId,
              animation: anim,
              isCharging: true,
            });
            await this.delay(anim.castTime ?? 200);
            EventBus.emit(Events.COMBAT_HIT, {
              targetId: tickSource.id,
              animation: anim,
              side: tickSource.side,
            });
          }
          EventBus.emit(Events.FLOATING_INFO, {
            targetId: event.sourceId,
            text: 'Charging...',
          });

          // Re-fire the bracket lock + TARGETED pip each tick so multi-turn
          // casts beat once per turn instead of fading into the background.
          if (event.targetId && event.targetId !== event.sourceId) {
            const victim = state.combatants.find(c => c.id === event.targetId);
            if (victim) {
              this.castingTargets.set(event.sourceId!, event.targetId);
              EventBus.emit(Events.COMBAT_CAST_TARGET_ENTER, {
                sourceId: event.sourceId,
                targetId: event.targetId,
                tint: anim.tint ?? 0xff4444,
                side: victim.side,
              });
              EventBus.emit(Events.FLOATING_INFO, {
                targetId: event.targetId,
                text: 'TARGETED!',
              });
            }
          }
        }
        break;
      }

      case 'summon': {
        if (!event.summonedId) break;

        const summoner = state.combatants.find(c => c.id === event.sourceId);
        const summonerSide = summoner?.side ?? 'enemy';
        // Hybrid summons (Seep) — damage step already animated the cast + hit, so skip
        // here to avoid double drama. For multi-mote bursts (Rupture) only fire the
        // summoner's animation on ordinal 0; subsequent motes spawn back-to-back.
        const isHybrid = event.summonHybrid === true;
        const isFirstOfBatch = (event.summonOrdinal ?? 0) === 0;
        const playSourceAnimation = !isHybrid && isFirstOfBatch;

        if (summoner && playSourceAnimation) {
          // Cast motion (channel pulse + caster glow)
          const anim = this.resolveAnimation(event);
          EventBus.emit(Events.COMBAT_ABILITY_USED, {
            sourceId: event.sourceId,
            animation: anim,
          });
          await this.delay(anim.castTime ?? 600);

          // Hit effect on the SUMMONER itself — sells "the rift tears open" for
          // void_rupture, "energy rises" for summon_rise. Without this, pure
          // summon abilities have no impact beat between cast and spawn.
          if (anim.hitEffect && anim.hitEffect !== 'none' && event.sourceId) {
            EventBus.emit(Events.COMBAT_HIT, {
              targetId: event.sourceId,
              animation: anim,
              side: summonerSide,
            });
            await this.delay(180);
          }
        } else if (summoner && !isHybrid) {
          // Subsequent motes in a burst — short pause so each spawn reads as its own beat.
          await this.delay(140);
        }

        const summonDefensiveTypes = event.summonDefensiveTypes ?? [];
        const primaryDefensiveType = summonDefensiveTypes[0] ?? 'steel';

        // Push summoned combatant into local state so subsequent events can find it.
        // Side mirrors the summoner — enemy summons (Rotmother adds) on the enemy side,
        // player-side minions (Conjuration/Manifestation) on the player side.
        state.combatants.push({
          id: event.summonedId,
          name: event.summonName ?? 'Summoned',
          element: primaryDefensiveType,
          hp: event.summonHp ?? 1,
          maxHp: event.summonMaxHp ?? 1,
          barriers: [],
          focus: 0,
          maxFocus: 0,
          focusRegen: 0,
          atk: 0, spd: 0,
          abilityIds: [],
          momentum: 0,
          isAlive: true,
          statusEffects: [],
          spriteKey: event.spriteKey ?? 'skarn',
          side: summonerSide,
          slotIndex: 0,
          isSummoned: true,
          ownerId: event.sourceId,
        });

        // Emit side-appropriate event for CombatScene + CombatHUD to spawn sprite and HP bar.
        const summonEvent = summonerSide === 'enemy'
          ? Events.COMBAT_ENEMY_SUMMONED
          : Events.COMBAT_ALLY_SUMMONED;
        EventBus.emit(summonEvent, {
          id: event.summonedId,
          spriteKey: event.spriteKey,
          name: event.summonName,
          hp: event.summonHp,
          maxHp: event.summonMaxHp,
          defensiveTypes: summonDefensiveTypes,
          ownerId: event.sourceId, // host's combatant id — used for adjacent placement on player side
        });

        // Show floating text
        await this.delay(400);
        EventBus.emit(Events.FLOATING_INFO, {
          targetId: event.summonedId,
          text: 'Summoned!',
        });
        break;
      }

      case 'focus_regen': {
        // Only update focus (do NOT pass hp — state.combatants has FINAL hp which causes desync)
        if (event.sourceId && event.focusAfter != null) {
          EventBus.emit(Events.HP_UPDATE, { id: event.sourceId, focus: event.focusAfter });
        }
        break;
      }

      case 'focus_drain': {
        // Enemy siphoning focus from a party member. Update the focus bar + show floating "-N FOCUS" text.
        if (event.targetId && event.focusAfter != null) {
          EventBus.emit(Events.HP_UPDATE, { id: event.targetId, focus: event.focusAfter });
        }
        if (event.targetId && (event.value ?? 0) > 0) {
          EventBus.emit(Events.FLOATING_FOCUS_DRAIN, {
            targetId: event.targetId,
            value: event.value ?? 0,
          });
        }
        break;
      }

      case 'tempo_gain': {
        // Update the tempo pill on the target + show floating "+N Tempo" text
        if (event.targetId && event.tempoAfter != null) {
          EventBus.emit(Events.HP_UPDATE, { id: event.targetId, tempo: event.tempoAfter });
          EventBus.emit(Events.FLOATING_TEMPO, {
            targetId: event.targetId,
            value: event.tempoGained ?? 0,
          });
        }
        break;
      }

      case 'tempo_consume': {
        // Clear the tempo pill (spender fired) — silent, the damage is its own feedback
        if (event.targetId) {
          EventBus.emit(Events.HP_UPDATE, { id: event.targetId, tempo: 0 });
        }
        break;
      }

      case 'fervor_gain': {
        // Update the party-wide Fervor pip track + show floating "+N FERVOR" text
        if (event.fervorAfter != null) {
          EventBus.emit(Events.FERVOR_UPDATE, { fervor: event.fervorAfter });
        }
        if (event.targetId && (event.fervorGained ?? 0) > 0) {
          EventBus.emit(Events.FLOATING_FERVOR, {
            targetId: event.targetId,
            value: event.fervorGained ?? 0,
          });
        }
        break;
      }

      case 'fervor_consume': {
        // Update the pip track — spender consumption or decay. Silent visually;
        // spender damage / decay log line carry the player feedback.
        if (event.fervorAfter != null) {
          EventBus.emit(Events.FERVOR_UPDATE, { fervor: event.fervorAfter });
        }
        break;
      }

      case 'break_fill': {
        // Per-enemy break bar fill update.
        if (event.targetId != null && event.breakFillAfter != null && event.breakThreshold != null) {
          EventBus.emit(Events.BREAK_UPDATE, {
            id: event.targetId,
            fill: event.breakFillAfter,
            threshold: event.breakThreshold,
          });
        }
        break;
      }

      case 'break_trigger': {
        // Threshold crossed — target flips to Broken. Reset bar, signal break event for visual flourish.
        if (event.targetId != null && event.breakThreshold != null) {
          EventBus.emit(Events.BREAK_UPDATE, {
            id: event.targetId,
            fill: 0,
            threshold: event.breakThreshold,
            broken: true,
          });
          // BROKEN! callout — the moment that matters gets its own large banner
          // above the target instead of just a log line.
          EventBus.emit(Events.FLOATING_INFO, {
            targetId: event.targetId,
            text: 'BROKEN!',
          });
        }
        break;
      }

      case 'flee_fail':
        // Just logged, no special visual
        break;
    }
  }

  private getDelay(event: CombatEvent): number {
    // Passive heals (Life on Hit, Leech, Soulstitch, Regen ticks) play as a quick
    // tick — no point holding the playback for a full beat per proc.
    if (event.type === 'heal' && !event.abilityId) return 250;
    switch (event.type) {
      case 'turn_start': return 800;
      case 'damage': return 800;
      case 'heal': return 800;
      case 'buff': return 1000;
      case 'dot_tick': return 600;
      case 'debuff': return 500;
      case 'cleanse': return 800;
      case 'effect_expire': return 300;
      case 'cast_start': return 1000;
      case 'cast_tick': return 600;
      case 'summon': return 1200;
      case 'focus_regen': return 300;
      case 'focus_drain': return 600;
      case 'defeated': return 900;
      case 'victory': return 400;
      case 'defeat': return 400;
      case 'enemy_transformed': return 900;
      default: return 300;
    }
  }

  private getDotTint(statusEffectId?: string): number {
    switch (statusEffectId) {
      case 'burn': return 0xff6600;
      case 'bleed': return 0xaa0000;
      case 'poison': return 0x88cc44;
      case 'decay': case 'wither': return 0x8844cc;
      default: return 0xaa44ff;
    }
  }

  private getDotHitEffect(statusEffectId?: string): string {
    switch (statusEffectId) {
      case 'bleed': case 'rend': return 'dot_bleed';
      case 'poison': case 'envenom': return 'dot_poison';
      case 'decay': case 'wither': return 'dot_decay';
      default: return 'dot_burn'; // burn and anything else
    }
  }

  private getDebuffTint(statusEffectId?: string): number {
    switch (statusEffectId) {
      case 'burn': case 'ignite': return 0xff4400;
      case 'bleed': case 'rend': return 0xaa0000;
      case 'poison': case 'envenom': return 0x88cc44;
      case 'decay': case 'wither': return 0x8844cc;
      case 'stagger': return 0xff2222;
      case 'soaked': return 0x44aaff;
      case 'marked': case 'hunters_mark': return 0xff8844;
      case 'fractured': return 0xccaa66;
      case 'sundered': return 0xcc6644;
      case 'weakened': return 0x9966cc;
      default: return 0x9944ff;
    }
  }

  private getDebuffHitEffect(statusEffectId?: string): string {
    switch (statusEffectId) {
      case 'burn': case 'ignite': return 'debuff_burn';
      case 'bleed': case 'rend': return 'debuff_bleed';
      case 'poison': case 'envenom': return 'debuff_poison';
      case 'decay': case 'wither': return 'debuff_decay';
      case 'soaked': return 'debuff_soaked';
      case 'marked': case 'hunters_mark': return 'debuff_marked';
      case 'stagger': return 'debuff_stagger';
      case 'fractured': return 'debuff_fractured';
      case 'sundered': return 'debuff_sundered';
      case 'weakened': return 'debuff_weakened';
      default: return 'debuff_flash';
    }
  }

  private getBuffHitEffect(statusEffectId?: string): string {
    switch (statusEffectId) {
      case 'fury': return 'buff_fury';
      case 'tailwind': return 'buff_tailwind';
      case 'venomcoat': return 'buff_venomcoat';
      case 'iron_wall': return 'buff_iron_wall';
      case 'brace': return 'buff_brace';
      case 'intercepting': return 'intercept_block';
      default: return 'buff_shimmer';
    }
  }

  private getBuffTint(statusEffectId?: string): number {
    switch (statusEffectId) {
      case 'fury': return 0xff4444;
      case 'tailwind': return 0x99ddcc;
      case 'venomcoat': return 0x88cc44;
      case 'iron_wall': return 0xaabbcc;
      case 'brace': return 0xc0c0d0;
      case 'intercepting': return 0xddcc44;
      default: return 0xffdd44;
    }
  }

  private delay(ms: number): Promise<void> {
    return new Promise(r => setTimeout(r, ms));
  }
}

export const EventPlaybackManager = new EventPlaybackManagerClass();
